/**
 * SheetMapEditor — teaching Symbia to read one client's spreadsheet (#174 part A).
 *
 * Pete, 2026-08-22: *"then we don't have the back and forth with the client to get
 * them to do the spreadsheet correctly! And it gives us a ton of flexibility and
 * speeds up everything."*
 *
 * ===========================================================================
 * ⭐ THE FIRST UPLOAD OF A CLIENT'S FILE *IS* THE SETUP STEP
 * ===========================================================================
 * There is no admin screen to visit first and no mapping to prepare in advance.
 * A scheduler opens the file they were sent, sees the columns we found and the
 * guesses we made, corrects them, and saves. ⚠️ THE PERSON DOING IT IS THE ONLY
 * ONE WHO CAN — "PCS" and "Power Class" mean something to the scheduler and
 * nothing to anybody configuring this a week earlier from a description.
 *
 * ⛔ AND IT IS WHY THE MAPPING SURVIVES A FORMAT CHANGE. When the client alters
 * their export the saved mapping breaks LOUDLY (the expected-header guard), and
 * the fix is this same screen, once, by whoever is holding the file.
 *
 * ===========================================================================
 * ⛔ EVERY GUESS IS CONFIRMED BY A PERSON BEFORE ONE VALUE IS READ
 * ===========================================================================
 * `suggestMapping` matches headings by synonym, which is fuzzy — and fuzzy
 * matching is correct HERE precisely because it is not committing anything. It
 * proposes; the scheduler disposes. ⚠️ That is the opposite of the carrier and WMS
 * matches, which run unattended and are therefore exact-only (arch §1R.4).
 */
import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, AlertTriangle, Loader2, Table2, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
    ApiCallError, saveClientSheetMap,
    type SheetInspectionDto, type SheetMapFieldDto,
} from '@/api/client';

/**
 * ⚠️ A SENTINEL, because Radix Select refuses an empty-string item value — it uses
 * '' internally for "nothing selected". Translated back at the boundary and never
 * sent to the API.
 */
const NONE = '__none__';
const CONSTANT = '__constant__';

/**
 * ⛔ FIELDS WHOSE FIXED VALUE IS A CHOICE, NOT A TYPING EXERCISE.
 *
 * Pete, 2026-08-24: *"Still can't select Inbound/Outbound if you can't map it
 * because the client doesn't, and probably won't, have it in the file."*
 *
 * ⚠️ "Same for every row…" DID cover this — but it then rendered a FREE-TEXT BOX.
 * For Direction that is both a chore and a trap: `Inbund`, `IN`, `inbound ` all
 * look fine on screen and are rejected row by row later, which is exactly the
 * 500-rejection screen this was supposed to prevent. ⭐ For a field with a closed
 * set of legal values, the value belongs IN THE SAME DROPDOWN as the columns —
 * one click, nothing to spell.
 *
 * ⚠️ Keyed by our field key, and deliberately NOT derived from anything the
 * client's file contains: this is OUR vocabulary, and the whole point is that
 * their file does not have it.
 */
const FIXED_VALUE_OPTIONS: Record<string, string[]> = {
    direction: ['Inbound', 'Outbound'],
};

/** Encodes "this field is a fixed value, and the value is X" in one Select item. */
const CONST_PREFIX = '__const__:';

const TRANSFORM_LABELS: Record<string, string> = {
    none: 'Use as written',
    // ⭐ The one that earned its place: Thornova's "Delivery appt" is the integer
    // 700, and without this every timed truck in their file arrives day-only.
    hhmmNumber: 'A time written as a number (700 → 07:00)',
    upperCase: 'Force UPPER CASE',
    digitsOnly: 'Digits only (strip brackets, dashes, spaces)',
};

interface SheetMapEditorProps {
    inspection: SheetInspectionDto;
    facilityCode: string;
    clientCode: string;
    clientName: string;
    getToken: () => Promise<string>;
    /** Fired after a successful save, so the caller can re-run the preview. */
    onSaved: () => void;
    onCancel: () => void;
}

type Draft = Record<string, { source: string; constant: string; transform: string }>;

export function SheetMapEditor({
    inspection, facilityCode, clientCode, clientName, getToken, onSaved, onCancel,
}: SheetMapEditorProps) {
    const sheets = inspection.sheets;
    const existing = inspection.existing;

    const [sheetName, setSheetName] = useState<string>(
        existing?.sheetName ?? sheets[0]?.name ?? '');
    const [headerRow, setHeaderRow] = useState<number>(
        existing?.headerRow ?? sheets[0]?.firstRows[0]?.rowNumber ?? 1);
    const [dateFilter, setDateFilter] = useState<string>(existing?.dateFilterHeader ?? '');
    const [busy, setBusy] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);

    const sheet = sheets.find(s => s.name === sheetName) ?? sheets[0];

    /**
     * ⭐ THE HEADINGS ON THE ROW THE SCHEDULER HAS CHOSEN, recomputed as they change
     * it. ⚠️ Not fetched again — `inspect` returned the top of every sheet precisely
     * so that picking the header row is instant and needs no round trip.
     */
    const headers = useMemo(() => {
        const row = sheet?.firstRows.find(r => r.rowNumber === headerRow);
        return (row?.values ?? []).map(v => (v === null ? '' : String(v).trim()));
    }, [sheet, headerRow]);

    const nonEmptyHeaders = useMemo(
        () => headers.filter(h => h !== ''), [headers]);

    /**
     * ⚠️ THE FIRST DATA ROW IS DERIVED, NOT ASKED FOR. In every client export seen
     * so far the data starts immediately under the headings, and a spare number box
     * is a spare thing to get wrong. ⛔ If a client ever ships a blank spacer row,
     * that row is skipped by the reader anyway — wholly empty rows never reach the
     * judge — so deriving it costs nothing even then.
     */
    const firstDataRow = headerRow + 1;

    // ---- the draft mapping ------------------------------------------------
    const [draft, setDraft] = useState<Draft>({});

    useEffect(() => {
        const next: Draft = {};
        for (const f of inspection.ourFields) {
            next[f.key] = { source: NONE, constant: '', transform: 'none' };
        }
        // ⭐ A SAVED MAPPING WINS OVER A GUESS. Re-opening this screen to fix one
        // column must not silently re-guess the other thirteen.
        if (existing) {
            for (const f of existing.fields) {
                if (!next[f.ourField]) continue;
                next[f.ourField] = {
                    source: f.constantValue !== null ? CONSTANT : (f.sourceHeader ?? NONE),
                    constant: f.constantValue ?? '',
                    transform: f.transform,
                };
            }
        } else {
            for (const s of inspection.suggested) {
                if (!next[s.ourField]) continue;
                next[s.ourField] = {
                    source: s.sourceHeader, constant: '', transform: s.transform,
                };
            }
        }
        setDraft(next);
    }, [inspection, existing]);

    const setField = (key: string, patch: Partial<Draft[string]>) =>
        setDraft(d => ({ ...d, [key]: { ...d[key], ...patch } }));

    const fieldsForSave = (): SheetMapFieldDto[] =>
        inspection.ourFields.flatMap(f => {
            const d = draft[f.key];
            if (!d) return [];
            if (d.source === CONSTANT) {
                if (!d.constant.trim()) return [];
                const row: SheetMapFieldDto = {
                    ourField: f.key, sourceHeader: null,
                    constantValue: d.constant.trim(),
                    // ⚠️ A fixed value is written as typed. Running a conversion over
                    // something a scheduler entered by hand would silently change what
                    // they wrote, on a screen that shows it back to them unchanged.
                    transform: 'none',
                };
                return [row];
            }
            if (d.source === NONE || !d.source) return [];
            const row: SheetMapFieldDto = {
                ourField: f.key, sourceHeader: d.source, constantValue: null,
                transform: d.transform as SheetMapFieldDto['transform'],
            };
            return [row];
        });

    const save = async () => {
        setBusy(true); setErrors([]);
        try {
            await saveClientSheetMap(await getToken(), {
                facilityCode,
                clientCode,
                label: sheet?.name ? `${clientName} — ${sheet.name}` : clientName,
                sheetName: sheetName || null,
                headerRow,
                firstDataRow,
                // ⛔ THE HEADINGS AS THEY ARE IN *THIS* FILE — the staleness guard.
                // Saving our own field names here instead would make the guard
                // compare our vocabulary to theirs and fire on every upload.
                expectedHeaders: nonEmptyHeaders,
                dateFilterHeader: dateFilter === NONE || !dateFilter ? null : dateFilter,
                fields: fieldsForSave(),
            });
            onSaved();
        } catch (err) {
            if (err instanceof ApiCallError) {
                // ⭐ EVERY problem at once — the server returns the full list, and
                // showing one at a time would make this a guessing game.
                const issues = (err.details as { issues?: string[] }).issues;
                setErrors(issues?.length ? issues : [err.details.error]);
            } else {
                setErrors(['The mapping could not be saved.']);
            }
        } finally { setBusy(false); }
    };

    const unmappedRequired = inspection.ourFields.filter(
        f => f.required && (() => {
            const d = draft[f.key];
            if (!d) return true;
            if (d.source === CONSTANT) return !d.constant.trim();
            return d.source === NONE || !d.source;
        })());

    if (!sheet) {
        return (
            <div className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                <div>We could not find any sheets in that workbook.</div>
            </div>
        );
    }

    return (
        /**
         * ⛔ `min-w-0` IS LOAD-BEARING, NOT TIDINESS. shadcn's DialogContent is a
         * CSS grid, and a grid item defaults to `min-width: auto` — it refuses to
         * shrink below its content's intrinsic width. The sample table is as wide
         * as the client's widest row, so the whole editor grew past the dialog and
         * the banner, the table and the field rows all bled off the right edge.
         * Pete, 2026-08-24: *"Spacing still off."*
         *
         * ⚠️ `overflow-auto` on the table alone cannot fix it — the ancestor has to
         * be allowed to be narrower than its child before anything will scroll.
         */
        <div className="min-w-0 space-y-4">
            <div className="rounded-md border bg-muted/30 p-3 text-sm">
                <div className="flex items-start gap-2">
                    <Wand2 className="mt-0.5 h-4 w-4 shrink-0" />
                    <div>
                        <div className="font-medium">
                            Teach us to read {clientName}&rsquo;s file
                        </div>
                        <div className="text-muted-foreground mt-0.5 text-xs">
                            Point each of our fields at one of their columns. We remember this
                            for {clientName}, so it only has to be done once — and again only
                            if they change their format.
                        </div>
                    </div>
                </div>
            </div>

            {/* ---- where the data is ---- */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 [&>*]:min-w-0">
                <div className="space-y-1.5">
                    <Label htmlFor="sm-sheet">Which sheet</Label>
                    <Select value={sheetName} onValueChange={(v) => {
                        setSheetName(v);
                        // ⚠️ The header row is per sheet; carrying row 4 across to a
                        // sheet whose headings are on row 1 would show empty columns
                        // and read as "this file has no headings".
                        const s = sheets.find(x => x.name === v);
                        setHeaderRow(s?.firstRows[0]?.rowNumber ?? 1);
                    }}>
                        <SelectTrigger id="sm-sheet"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {sheets.map(s => (
                                <SelectItem key={s.name} value={s.name}>
                                    {s.name} ({s.rowCount} rows)
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-1.5">
                    <Label htmlFor="sm-row">Which row has the column headings</Label>
                    <Select value={String(headerRow)} onValueChange={(v) => setHeaderRow(Number(v))}>
                        <SelectTrigger id="sm-row"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {sheet.firstRows.map(r => (
                                <SelectItem key={r.rowNumber} value={String(r.rowNumber)}>
                                    Row {r.rowNumber}: {r.values.slice(0, 4)
                                        .map(v => (v === null ? '' : String(v)))
                                        .filter(Boolean).join(' | ').slice(0, 48) || '(blank)'}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <p className="text-muted-foreground text-xs">
                        Their data starts on row {firstDataRow}.
                    </p>
                </div>
            </div>

            {/* ---- what we can see ---- */}
            <div className="space-y-1.5">
                <div className="flex items-center gap-1.5 text-xs font-medium">
                    <Table2 className="h-3.5 w-3.5" />
                    What their file looks like
                </div>
                <div className="max-h-40 min-w-0 overflow-x-auto overflow-y-auto rounded-md border">
                    <table className="w-full text-xs">
                        <tbody>
                            {sheet.firstRows.slice(0, 5).map(r => (
                                <tr key={r.rowNumber}
                                    className={r.rowNumber === headerRow ? 'bg-muted/60 font-medium' : ''}>
                                    <td className="text-muted-foreground px-2 py-1 tabular-nums">
                                        {r.rowNumber}
                                    </td>
                                    {r.values.slice(0, 10).map((v, i) => (
                                        <td key={i} className="whitespace-nowrap px-2 py-1">
                                            {v === null ? '' : String(v).slice(0, 22)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ---- ⭐ the one that stops a rolling schedule becoming 1,208 trucks ---- */}
            <div className="space-y-1.5">
                <Label htmlFor="sm-datefilter">
                    Which column says what day a row is for
                </Label>
                <Select value={dateFilter || NONE}
                        onValueChange={(v) => setDateFilter(v === NONE ? '' : v)}>
                    <SelectTrigger id="sm-datefilter">
                        <SelectValue placeholder="This file only ever holds one day" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={NONE}>This file only ever holds one day</SelectItem>
                        {nonEmptyHeaders.map(h => (
                            <SelectItem key={h} value={h}>{h}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <p className="text-muted-foreground text-xs">
                    {dateFilter
                        ? `We will take only the rows whose ${dateFilter} matches the date you `
                          + 'chose, and tell you how many were for other days.'
                        : 'Leave this alone only if the client sends one day per file — otherwise '
                          + 'their whole schedule becomes appointments in one go.'}
                </p>
            </div>

            {/* ---- the mapping itself ---- */}
            <div className="space-y-1.5">
                <div className="text-xs font-medium">Our fields ← their columns</div>
                <div className="max-h-80 space-y-2 overflow-auto rounded-md border p-2">
                    {inspection.ourFields.map(f => {
                        const d = draft[f.key] ?? { source: NONE, constant: '', transform: 'none' };
                        const isConst = d.source === CONSTANT;
                        const fixedOptions = FIXED_VALUE_OPTIONS[f.key] ?? [];
                        return (
                            <div key={f.key} className="grid grid-cols-12 items-start gap-2">
                                <div className="col-span-4 pt-2">
                                    <div className="text-xs font-medium">
                                        {f.header}
                                        {f.required && <span className="text-destructive"> *</span>}
                                    </div>
                                    <div className="text-muted-foreground text-[11px] leading-tight">
                                        {f.hint}
                                    </div>
                                </div>
                                <div className="col-span-5 min-w-0">
                                    <Select
                                        /* ⭐ A CHOSEN FIXED VALUE SHOWS AS ITSELF, not as
                                           "Same for every row…" with the answer hidden in a
                                           box beside it. */
                                        value={isConst && fixedOptions.includes(d.constant)
                                               ? CONST_PREFIX + d.constant
                                               : d.source}
                                        onValueChange={(v) => {
                                            if (v.startsWith(CONST_PREFIX)) {
                                                setField(f.key, {
                                                    source: CONSTANT,
                                                    constant: v.slice(CONST_PREFIX.length),
                                                    transform: 'none',
                                                });
                                                return;
                                            }
                                            // ⚠️ Leaving the fixed-value branch clears the value.
                                            // A stale constant left behind would be saved the next
                                            // time somebody flipped back, without being re-chosen.
                                            setField(f.key, { source: v, constant: '' });
                                        }}>
                                        <SelectTrigger className="h-8 text-xs">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value={NONE}>— not in their file —</SelectItem>
                                            {/* ⭐ THE VALUES THEMSELVES, IN THE SAME LIST AS THE
                                                COLUMNS. Thornova send no Direction column, and they
                                                are not going to start; one click has to be able to
                                                say "all of these are inbound". */}
                                            {fixedOptions.map(v => (
                                                <SelectItem key={v} value={CONST_PREFIX + v}>
                                                    {v} — every row
                                                </SelectItem>
                                            ))}
                                            {/* ⚠️ Free text stays for fields with no closed set —
                                                a fixed reference or door, say — but it must not be
                                                offered for Direction, where it is only a way to
                                                misspell one of two words. */}
                                            {fixedOptions.length === 0 && (
                                                <SelectItem value={CONSTANT}>
                                                    Same for every row…
                                                </SelectItem>
                                            )}
                                            {nonEmptyHeaders.map(h => (
                                                <SelectItem key={h} value={h}>{h}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="col-span-3 min-w-0">
                                    {isConst && fixedOptions.length === 0 ? (
                                        <Input
                                            className="h-8 text-xs"
                                            placeholder="value"
                                            value={d.constant}
                                            onChange={(e) => setField(f.key, { constant: e.target.value })}
                                        />
                                    ) : isConst ? null : d.source !== NONE ? (
                                        <Select value={d.transform}
                                                onValueChange={(v) => setField(f.key, { transform: v })}>
                                            <SelectTrigger className="h-8 text-xs">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {Object.entries(TRANSFORM_LABELS).map(([k, label]) => (
                                                    <SelectItem key={k} value={k}>{label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    ) : null}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* ⚠️ SAID BEFORE THEY PRESS SAVE. The server refuses this too — but
                finding out after a round trip, on a screen you have to scroll back
                up, is a worse way to learn it. */}
            {unmappedRequired.length > 0 && (
                <div className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                    <div>
                        <div className="font-medium">
                            {unmappedRequired.map(f => f.header).join(', ')} still
                            {unmappedRequired.length === 1 ? ' needs' : ' need'} a source
                        </div>
                        <div className="mt-0.5 text-xs">
                            This facility requires {unmappedRequired.length === 1 ? 'it' : 'them'} on
                            every appointment. If their file does not have a column for it, use
                            <strong> Same for every row</strong> — that is how Direction is usually set.
                        </div>
                    </div>
                </div>
            )}

            {errors.length > 0 && (
                <div className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                    <ul className="space-y-0.5">
                        {errors.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                </div>
            )}

            <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>
                    Back
                </Button>
                <Button size="sm" onClick={() => void save()} disabled={busy}>
                    {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                    Save and read the file
                </Button>
            </div>
        </div>
    );
}
